import { Injectable } from '@angular/core';

import { pipeFns } from './../../utils/pipe-fns';
import { AwningTypes } from './models/awning-types';
import { Xyz } from '../../models/2d3d/xyz';

declare const THREE: any;

export interface AwningExtensionConfig {
  id: string;
  position: Xyz;
  rotation: Xyz;
  basePosition: Xyz;
}

export interface AwningConfig {
  id: string;
  position: Xyz;
  rotation: Xyz;
  color?: any;
  texture?: any;
  extensions?: AwningExtensionConfig[];
}

@Injectable()
export class AwningService {
  box = new THREE.Box3();

  constructor() {}

  // getters start
  getAwningEntities(products) {
    return products.filter(product => product.productType === 'Awning');
  }

  getBaseAwningEntities(products) {
    return products.filter(
      product => product.productType === 'Awning' && product.awningType.match(/^(.+BaseKit)$/)
    );
  }

  getExtensionAwningEntity(products, baseAwning: AwningTypes, awningPosition: 'left' | 'right') {
    const awningPositionType = awningPosition === 'left' ? 'Left' : 'Right';
    const awningType = baseAwning.replace('BaseKit', '');
    const regx = new RegExp(`^(${awningType}.+${awningPositionType})$`);
    return products.filter(
      product => product.productType === 'Awning' && product.awningType.match(regx)
    );
  }

  getDirectParent(awning) {
    let parent = null;
    while (awning.parent && !parent) {
      if (awning.parent.name === 'Awning' || awning.parent.name === 'wall') {
        parent = awning.parent;
      }
    }
    return parent;
  }

  getBaseAwning(awning) {
    if (this.isBaseAwning(awning)) return awning;
    return this.getDirectParent(awning);
  }

  getSizes(awning) {
    const getBoundingBox = awning => {
      const box = new THREE.Box3();
      awning.traverse(child => {
        if (!child.geometry) {
          return;
        }

        child.geometry.computeBoundingBox();
        const childBox = child.geometry.boundingBox.clone();
        if (this.isAwning(child.parent) && !this.isBaseAwning(child.parent)) {
          child.parent.updateMatrix();
          childBox.applyMatrix4(child.parent.matrix);
        }
        box.union(childBox);
      });

      return box;
    };

    const sizes = new THREE.Vector3();
    const box = getBoundingBox(awning);
    box.getSize(sizes);
    return sizes;
  }

  getCenter(awning) {
    const center = new THREE.Vector3();
    this.box.setFromObject(awning);
    this.box.getCenter(center);
    return center;
  }

  getAwningPositionType(awningType): 'left' | 'right' {
    return awningType
      .match(/[A-Z][a-z]+/g)
      .slice(-1)[0]
      .toLowerCase();
  }

  getGroupCenterFromFuturePosition(baseAwning, position) {
    // awning group center might be shifted,
    // if we add another awning to the right or
    // left. This method is used to find center of awning
    // when, for e. g. dragging it
    const baseCenterPosition = this.getCenter(baseAwning);
    const draggedObjPositionCorrectionShift = pipeFns(position =>
      position.sub(baseAwning.getWorldPosition(new THREE.Vector3()))
    )(baseCenterPosition.clone());
    return pipeFns(position => position.add(draggedObjPositionCorrectionShift))(position.clone());
  }

  getExtensions(awning) {
    return awning.children.filter(c => c && this.isAwning(c));
  }

  getAwningsByFloorAndWallId(floor: any, wallId: number): any[] {
    let awnings = [];
    const walls = floor.children.filter(element => element.name === 'wall');
    const wall = walls.find(element => element.userData.uuid === wallId);
    awnings = wall && wall.children.filter(obj => this.isAwning(obj));
    return awnings;
  }
  // getters end

  // checkers start
  isAwning(object) {
    return (
      object &&
      object.userData &&
      object.userData.entity &&
      object.userData.entity.productType === 'Awning'
    );
  }

  isBaseAwning(awning): boolean {
    // base awning is
    // the main object,
    // that might contain
    // left and right extensions
    return (
      awning.userData &&
      awning.userData.entity &&
      awning.userData.entity.awningType.match(/^(.+BaseKit)$/)
    );
  }

  isLastInRow(awning) {
    let lastInRow = false;
    const parent = this.getDirectParent(awning);
    if (parent) {
      const groupSizes = this.getSizes(parent);
      const groupCenter = this.getCenter(parent);
      const sizes = this.getSizes(awning);
      const center = this.getCenter(awning);
      const groupCenterToCenter = center.sub(groupCenter);
      lastInRow =
        Math.abs(
          Math.round(groupCenterToCenter.length() + sizes.x / 2) - Math.round(groupSizes.x / 2)
        ) <= 5;
    }
    return lastInRow;
  }

  isFromTheSameGroup(awning, toCheckAwning): boolean {
    return toCheckAwning.parent === awning;
  }
  // checkers end
}



// WEBPACK FOOTER //
// ./src/app/services/awning/awning.service.ts